import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { COURSES } from './SearchForm';

const todayIso = () => new Date().toISOString().slice(0, 10);
const addDays = (iso, n) => {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

const prettyDate = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString('en-CA', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

const prettyTime = (t) => {
  if (!t) return null;
  const [h, m] = t.split(':').map(Number);
  const period = h >= 12 ? 'pm' : 'am';
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')}${period}`;
};

const EMPTY = {
  courseIds: [],
  dateFrom: todayIso(),
  dateTo: addDays(todayIso(), 7),
  timeFrom: '06:00',
  timeTo: '12:00',
  players: '2',
};

function Alerts() {
  const { currentUser } = useAuth();
  const [searchParams] = useSearchParams();

  // Arriving from a search that came up empty: prefill what they already asked
  // for, so setting the alert is one click rather than re-entering everything.
  const prefill = useMemo(() => {
    const courses = (searchParams.get('courses') || '').split(',').filter(Boolean);
    if (!courses.length) return null;
    const date = searchParams.get('date');
    const valid = date && /^\d{4}-\d{2}-\d{2}$/.test(date) && date >= todayIso();
    return {
      ...EMPTY,
      courseIds: courses.filter((id) => COURSES[id]),
      dateFrom: valid ? date : EMPTY.dateFrom,
      dateTo: valid ? date : EMPTY.dateTo,
      players: searchParams.get('players') || EMPTY.players,
    };
  }, [searchParams]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(prefill || EMPTY);
  const [courseQuery, setCourseQuery] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data: res } = await httpsCallable(functions, 'listWatches')();
      setData(res);
    } catch (err) {
      setError(err.message || 'Could not load your alerts.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (currentUser) load();
    else setLoading(false);
  }, [currentUser, load]);

  const atLimit = data && data.watches.length >= data.maxWatches;

  const visibleCourses = useMemo(() => {
    const q = courseQuery.trim().toLowerCase();
    return Object.entries(COURSES).filter(
      ([, c]) =>
        !q ||
        c.name.toLowerCase().includes(q) ||
        (c.municipality || '').toLowerCase().includes(q)
    );
  }, [courseQuery]);

  const toggleCourse = (id) =>
    setForm((f) => ({
      ...f,
      courseIds: f.courseIds.includes(id)
        ? f.courseIds.filter((c) => c !== id)
        : [...f.courseIds, id],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await httpsCallable(functions, 'createWatch')({
        courseIds: form.courseIds,
        dateFrom: form.dateFrom,
        dateTo: form.dateTo,
        timeFrom: form.timeFrom,
        timeTo: form.timeTo,
        players: Number(form.players),
      });
      setForm(EMPTY);
      setCourseQuery('');
      await load();
    } catch (err) {
      setError(err.message || 'Could not create that alert.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    setError('');
    try {
      await httpsCallable(functions, 'deleteWatch')({ id });
      await load();
    } catch (err) {
      setError(err.message || 'Could not delete that alert.');
    }
  };

  if (!currentUser) {
    return (
      <div className="mx-auto max-w-md px-4 py-16 text-center">
        <h1 className="tiu-h1 mb-3">Tee time alerts</h1>
        <p className="tiu-muted mb-6">
          Tell us the course, day and time you want. We'll watch the tee sheet and email you
          the moment something opens — including cancellations.
        </p>
        <Link to="/login" className="tiu-btn tiu-btn-lg tiu-btn-primary">Sign in to set an alert</Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <h1 className="tiu-h1">Tee time alerts</h1>
      <p className="tiu-muted mb-6">
        We check the courses you pick and email you as soon as a matching time appears.
        {data && ` ${data.watches.length} of ${data.maxWatches} alerts used.`}
      </p>

      {prefill && prefill.courseIds.length > 0 && (
        <div className="tiu-card-flat p-3 mb-5 text-sm">
          Prefilled from your search — {prefill.courseIds.length}{' '}
          {prefill.courseIds.length === 1 ? 'course' : 'courses'} on {prettyDate(prefill.dateFrom)}.
          Adjust anything below before creating it.
        </div>
      )}

      {error && <div className="tiu-alert-error mb-5">{error}</div>}

      {/* Existing watches ------------------------------------------------- */}
      {loading && <div className="tiu-card p-8 text-center tiu-muted">Loading…</div>}

      {!loading && data && data.watches.length > 0 && (
        <div className="flex flex-col gap-3 mb-8">
          {data.watches.map((w) => (
            <div key={w.id} className="tiu-card p-4 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="font-medium">
                  {w.courseNames.length <= 2
                    ? w.courseNames.join(' · ')
                    : `${w.courseNames[0]} + ${w.courseNames.length - 1} more`}
                </p>
                <p className="tiu-muted text-sm mt-1">
                  {w.dateFrom === w.dateTo
                    ? prettyDate(w.dateFrom)
                    : `${prettyDate(w.dateFrom)} – ${prettyDate(w.dateTo)}`}
                  {w.timeFrom && ` · ${prettyTime(w.timeFrom)}–${prettyTime(w.timeTo)}`}
                  {` · ${w.players} ${w.players === 1 ? 'player' : 'players'}`}
                </p>
                <p className="tiu-muted text-sm mt-1">
                  {w.notificationCount
                    ? `${w.notificationCount} alert${w.notificationCount === 1 ? '' : 's'} sent`
                    : 'No matches yet — we’re watching.'}
                </p>
              </div>
              <button onClick={() => remove(w.id)} className="tiu-btn tiu-btn-secondary flex-shrink-0">
                Delete
              </button>
            </div>
          ))}
        </div>
      )}

      {/* New watch -------------------------------------------------------- */}
      {!loading && (
        <form onSubmit={submit} className="tiu-card p-5">
          <h2 className="tiu-h2 mb-4">New alert</h2>

          {atLimit ? (
            <p className="tiu-muted">
              You've used all {data.maxWatches} of your alerts. Delete one to add another
              {data.tier === 'free' && ', or upgrade for more'}.
            </p>
          ) : (
            <>
              <label className="tiu-label" htmlFor="course-filter">Courses</label>
              <input
                id="course-filter"
                value={courseQuery}
                onChange={(e) => setCourseQuery(e.target.value)}
                placeholder="Search courses or cities"
                className="tiu-input mb-2"
              />
              <div className="tiu-scroll tiu-course-list p-2 mb-1" style={{ maxHeight: 220 }}>
                {visibleCourses.map(([id, course]) => {
                  const on = form.courseIds.includes(id);
                  return (
                    <button
                      type="button"
                      key={id}
                      onClick={() => toggleCourse(id)}
                      aria-pressed={on}
                      className={`tiu-option ${on ? 'is-selected' : ''}`}
                    >
                      <span className={`tiu-check ${on ? 'is-on' : ''}`} aria-hidden="true">
                        {on && (
                          <svg viewBox="0 0 12 10" fill="none">
                            <path d="M1 5.2 4.2 8.4 11 1.6" stroke="currentColor" strokeWidth="2"
                              strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="tiu-option-name">{course.name}</span>
                        <span className="tiu-option-meta">{course.municipality}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
              <p className="tiu-muted text-sm mb-4">{form.courseIds.length} selected</p>

              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="tiu-label" htmlFor="dateFrom">From</label>
                  <input id="dateFrom" type="date" className="tiu-input" min={todayIso()}
                    max={addDays(todayIso(), 30)} value={form.dateFrom}
                    onChange={(e) => setForm({ ...form, dateFrom: e.target.value })} />
                </div>
                <div>
                  <label className="tiu-label" htmlFor="dateTo">To</label>
                  <input id="dateTo" type="date" className="tiu-input" min={form.dateFrom}
                    max={addDays(todayIso(), 30)} value={form.dateTo}
                    onChange={(e) => setForm({ ...form, dateTo: e.target.value })} />
                </div>
                <div>
                  <label className="tiu-label" htmlFor="timeFrom">Earliest tee time</label>
                  <input id="timeFrom" type="time" className="tiu-input" value={form.timeFrom}
                    onChange={(e) => setForm({ ...form, timeFrom: e.target.value })} />
                </div>
                <div>
                  <label className="tiu-label" htmlFor="timeTo">Latest tee time</label>
                  <input id="timeTo" type="time" className="tiu-input" value={form.timeTo}
                    onChange={(e) => setForm({ ...form, timeTo: e.target.value })} />
                </div>
              </div>

              <div className="mb-4">
                <label className="tiu-label" htmlFor="players">Players</label>
                <select id="players" className="tiu-input" value={form.players}
                  onChange={(e) => setForm({ ...form, players: e.target.value })}>
                  {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>

              <button type="submit" className="tiu-btn tiu-btn-lg tiu-btn-primary"
                disabled={saving || !form.courseIds.length}>
                {saving ? 'Creating…' : 'Create alert'}
              </button>
              <p className="tiu-muted text-sm mt-3">
                Alerts go to {currentUser.email}. We'll email you within a minute with anything
                that already matches, then whenever something new opens. Text alerts are coming soon.
              </p>
            </>
          )}
        </form>
      )}
    </div>
  );
}

export default Alerts;
